import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DailyChallengeView from './DailyChallengeView';
import LoadingScreen from './LoadingScreen';
import InstructionsModal from './InstructionsModal';
import { StorageManager } from '../utils/storageManager';
import { runStorageMigrationIfNeeded } from '../utils/migrationManager';
import { fixChallengeScore } from '../utils/fixChallengeScore';
import { STORAGE_KEYS, DEFAULT_CHALLENGE_DATE } from '../constants';

// Lazy load the CalendarModal after static imports
const CalendarModal = lazy(() => import('./CalendarModal'));

// Helper function to validate YYYY-MM-DD format
const isValidDateString = (dateStr: string | undefined): dateStr is string => {
  return typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
};

const GameManager: React.FC = () => {
  // Get date from URL, could be undefined if path is just /
  const { date: urlDate } = useParams<{ date?: string }>();
  const navigate = useNavigate(); // Keep navigate for calendar selection

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [activeChallengeDate, setActiveChallengeDate] = useState<string>('');
  const [isCurrentDaily, setIsCurrentDaily] = useState(true); // New state
  const hasRunFormatCleanup = useRef(false);

  // ADD THIS USEEFFECT HOOK
  useEffect(() => {
    const checkAndFixScores = () => {
      const fixFlag = `${STORAGE_KEYS.SCORE_FIX_FLAG_PREFIX}2025-06-13`;
      if (localStorage.getItem(fixFlag) === 'true') {
        return;
      }

      const corrections = [
        { roundIndex: 1, correctYear: 1929 }, // UPDATE THIS
      ];

      // --- THIS IS THE BUG FIX ---
      // Run the fix and check if it actually changed anything.
      const wasFixed = fixChallengeScore('2025-06-13', corrections);

      // Always set the flag so we don't try again.
      localStorage.setItem(fixFlag, 'true');

      // If data was changed, force a reload to ensure all components re-read from storage.
      if (wasFixed) {
        window.location.reload();
      }
      // --- END OF BUG FIX ---
    };

    checkAndFixScores();
  }, []); // Empty dependency array means this runs once on component mount

  // Effect 1: Determine Active Date and isCurrentDaily based on URL, handle future dates
  useEffect(() => {
    setIsLoading(true);

    const todayET = StorageManager.getTodayDateET();
    let dateToLoad = DEFAULT_CHALLENGE_DATE;
    let isActuallyCurrentDaily = false;

    if (urlDate) {
      if (!isValidDateString(urlDate)) {
        navigate('/', { replace: true });
        return;
      }

      if (urlDate > todayET) {
        navigate('/', { replace: true });
        return;
      } else {
        dateToLoad = urlDate;
        isActuallyCurrentDaily = dateToLoad === todayET;
      }
    }

    setIsCurrentDaily(isActuallyCurrentDaily);
    setActiveChallengeDate(dateToLoad);
    setIsLoading(false);
  }, [urlDate, navigate]);

  // Effect 2: Run Migrations/Cleanups (separated for clarity)
  useEffect(() => {
    try {
      runStorageMigrationIfNeeded();
    } catch (migrationError) {
      // Error handling without console.log
    }

    if (!hasRunFormatCleanup.current) {
      try {
        StorageManager.deduplicateGameHistory();
        hasRunFormatCleanup.current = true;
      } catch (error) {
        // Error handling without console.log
      }
    }
  }, []);

  // Handle date selection from Calendar
  const handleCalendarDateSelection = useCallback(
    (date: string) => {
      setShowCalendarModal(false);

      const todayETDateString = StorageManager.getTodayDateET();
      let targetPath: string;
      if (date === todayETDateString) {
        targetPath = '/';
      } else {
        targetPath = `/challenge/${date}`;
      }

      const currentPath = urlDate ? `/challenge/${urlDate}` : '/';

      if (targetPath !== currentPath) {
        navigate(targetPath);
      }
    },
    [navigate, urlDate]
  );

  // Show Instructions Modal on first visit
  useEffect(() => {
    const hasSeen = localStorage.getItem(STORAGE_KEYS.HAS_SEEN_INSTRUCTIONS);
    if (!hasSeen) {
      setShowInstructions(true);
    }
  }, []);

  const handleHideInstructions = () => {
    setShowInstructions(false);
    localStorage.setItem(STORAGE_KEYS.HAS_SEEN_INSTRUCTIONS, 'true');
  };

  const renderContent = () => {
    if (isLoading || !activeChallengeDate) {
      // Check activeChallengeDate is set
      return <LoadingScreen />;
    }
    // Keep this check if fetchAvailableDates is implemented later
    // if (!hasChallengeAvailable) {
    //   return <div className="p-4 text-center text-red-500">Challenge not available for this date.</div>;
    // }

    // Render DailyChallengeView once activeChallengeDate is determined
    return (
      <DailyChallengeView
        key={activeChallengeDate} // Use date as key to force remount on date change
        selectedDate={activeChallengeDate}
        isCurrentDaily={isCurrentDaily} // Pass the new prop
        onShowInstructions={() => setShowInstructions(true)}
        onShowCalendar={() => setShowCalendarModal(true)}
      />
    );
  };

  return (
    <div className="relative">
      {renderContent()}

      {/* Wrap CalendarModal with Suspense */}
      {showCalendarModal && (
        <Suspense fallback={<div>Loading Calendar...</div>}>
          <CalendarModal
            isOpen={showCalendarModal}
            onClose={() => setShowCalendarModal(false)}
            onSelectDate={handleCalendarDateSelection}
            selectedDate={activeChallengeDate}
          />
        </Suspense>
      )}

      <InstructionsModal
        isOpen={showInstructions}
        onClose={handleHideInstructions}
      />
    </div>
  );
};

export default GameManager;
